import React, { useEffect, useRef, useState, useReducer } from "react"
import { useQuery } from "react-query"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import axios from "axios"
import cn from "classnames"
import customStyles from "./styles"
import { debouncer } from "utils/jsUtils"
import DropdownIndicatorForSelect from "components/DropdownIndicatorForSelect"

type LOADING_NUMBER = {
  terminal_label: string
  supplier_label: string
  supplier: number
  terminal: any
}

type RECORDER = { [key: string]: { [key: string]: boolean } }

type OPTIONS = {
  [key: string]: {
    label: string
    id: number
    terminal_label: string
    source_name: string
    terminal: any
  }[]
}

const fetchLoadingNumbers =
  (supplier?: number, terminal?: number, location?: number) => async () => {
    let url = "loading-numbers/?page_size=9999"
    if (!supplier) return {}

    if (supplier) url += `&supplier=${supplier}`
    if (terminal) url += `&terminal=${terminal}`
    if (location) url += `&location_id=${location}`
    const { data } = await axios.get(url)
    const output = {} as {
      [key: number | string]: {
        wholesaler?: string
        description?: string
        loading_number?: string
        loading_number_id?: string
        value: number | string
        label: string
      }
    }
    data.results.forEach((item: any) => {
      let label = ""
      if (item.wholesaler && item.wholesaler !== "-")
        label += `${item.wholesaler} - `
      if (item.description && item.description !== "-")
        label += `${item.description} : `
      if (item.loading_number && item.loading_number !== "-")
        label += `${item.loading_number}`

      if (label.endsWith("- ") || label.endsWith(": "))
        label = label.slice(0, -2)

      output[`_${item.id}`] = {
        value: item.id,
        wholesaler: item.wholesaler,
        description: item.description,
        loading_number: item.loading_number,
        loading_number_id: item.id,
        label,
      }
    })

    return output
  }

const LoadingNumberPicker = (props: {
  selected?: any
  product?: number
  terminal?: number
  flush?: boolean
  placeholder?: string
  style?: any
  supplier?: number
  location?: number
  className?: string
  isClearable?: boolean
  multi?: boolean
  forcePreselect?: boolean
  label?: string
  quantity?: number
  errorText?: string
  onSelect?: (item: any) => void
}) => {
  const selectedRef = useRef<unknown>(null)
  const [show, setShow] = useState(true)

  const { data, isLoading, refetch, isFetching, isRefetching } = useQuery(
    ["loading_numbers", props.supplier, props.terminal, props.location],
    fetchLoadingNumbers(props.supplier, props.terminal, props.location),
    {
      refetchOnWindowFocus: false,
    }
  )
  const errorColor = props.errorText ? "red" : "#cbdfea"
  useEffect(() => {
    selectedRef.current = null
    refetch()
  }, [props.supplier, props.terminal, props.location])

  const onCreate = async (label: string) => {
    const body = {
      supplier: props.supplier,
      terminal: props.terminal,
      loading_number: label,
    }
    await axios.post("loading-numbers/", body)

    refetch()
  }

  const onSelect = (data: any) => {
    props?.onSelect?.(data)
    if (data?.__isNew__) {
      onCreate(data.label)
    }
  }

  const getDefaultValue = () => {
    if (!data) return null

    const defaultValues = Object.values((data ?? {}) as any)?.filter(
      (x: any) => {
        const isFiltered =
          props.selected?.length > 3
            ? // using this filter only if selected could be a loading number instead of an id. loading numbers are all >3 chars is the assumption
              x.loading_number.startsWith(props.selected) ||
              x.value === props.selected
            : x.value === props.selected
        return props.multi
          ? ((props.selected as number[]) ?? [])?.some((p) => p === x.value)
          : isFiltered
        /* 
        Not idea why the starts with check is needed here. Leaving it here as it was for future reference
        Isanio review this if you cross across this comment
        props.multi
        ? ((props.selected as number[]) ?? [])?.some((p) => p === x.value)
        : x.loading_number.startsWith(props.selected) ||
        x.value === props.selected
        */
      }
    )

    return props.selected
      ? props.multi
        ? defaultValues
        : defaultValues?.[0]
      : selectedRef.current
  }

  useEffect(() => {
    const keys = Object.keys(data ?? {})
    if (!keys?.length) return

    if (!props.forcePreselect && keys.length > 1) return
    if (props.forcePreselect && !props.selected) return

    const loadingNumber = getDefaultValue() ?? data?.[keys[0]]
    setShow(false)
    onSelect(loadingNumber)
    selectedRef.current = loadingNumber
    if (!props.selected && loadingNumber)
      debouncer(() => onSelect(loadingNumber), 100)
    // refetch()
    // setTimeout(() => setShow(true), 100)
    setShow(true)
  }, [JSON.stringify(data ?? {}), props?.quantity])

  if (!props.terminal) return <React.Fragment />
  if (!props.supplier) return <React.Fragment />
  if (isLoading || isFetching) return <React.Fragment />
  // if (!show) return <React.Fragment />

  const propStyle = props.style ?? {}
  const style = props.flush ? { marginRight: 0, ...propStyle } : propStyle
  style.border = `1px solid ${errorColor}`
  const defaultValue = getDefaultValue()

  return (
    <div className={cn("flex-c-box", props.className)}>
      {!props.label ? null : (
        <>
          <span style={{ color: "#486f88", fontWeight: "bold" }}>
            {props.label}
          </span>
          <div style={{ marginTop: 4 }} />
        </>
      )}

      <Select
        components={
          props.errorText
            ? { DropdownIndicator: DropdownIndicatorForSelect }
            : {}
        }
        isMulti={props.multi}
        isClearable={props.isClearable}
        placeholder={props.placeholder ?? "Enter Loading Number"}
        options={Object.values((data ?? {}) as any)}
        // onCreateOption={onCreate}
        onChange={onSelect}
        styles={customStyles(style)}
        value={defaultValue}
        // value={created}
      />
    </div>
  )
}

export default LoadingNumberPicker
